import { useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import { fetchSite } from "../actions/siteActions";
import { useImage } from "../contexts/ImageContext";
import { fetchWishlist } from "../actions/wishlistActions";
import { SITE_DESCRIPTION, SITE_NAME_FULL } from "../variables";

const SiteHead = () => {
  const dispatch = useDispatch();
  const location = useLocation();

  // get site settings
  const { settings } = useSelector((state) => state.site);
  const { getImageUrl } = useImage();

  // Check if we're on a product page (product pages will have their own Helmet)
  const isProductPage = location.pathname.startsWith("/product/");

  useEffect(() => {
    dispatch(fetchSite());
    dispatch(fetchWishlist(1));
  }, [dispatch]);

  // get wishlist items
  const { results = [] } = useSelector((state) => state.wishlist);
  // save the wishlist items to local storage
  useEffect(() => {
    localStorage.setItem("wishlist", JSON.stringify(results));
  }, [results]);

  useEffect(() => {
    if (settings?.favicon) {
      let link = document.querySelector("link[rel='icon']");
      if (!link) {
        link = document.createElement("link");
        link.rel = "icon";
        document.head.appendChild(link);
      }
      link.href = getImageUrl(settings.favicon);
    }
  }, [settings, getImageUrl]);

  // Default meta tags for non-product pages
  // Product pages will override these with their own Helmet
  if (isProductPage) {
    return null;
  }

  const siteUrl = window.location.origin;
  const defaultImage = `${siteUrl}/assets/logo.png`;

  const pageTitle = settings?.name || SITE_NAME_FULL;
  const pageDescription = SITE_DESCRIPTION;

  return (
    <Helmet>
      <title>{pageTitle}</title>
      <meta name="description" content={pageDescription} />

      {/* Open Graph / Facebook / WhatsApp / LinkedIn */}
      <meta property="og:type" content="website" />
      <meta property="og:url" content={siteUrl} />
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={pageDescription} />
      <meta property="og:image" content={defaultImage} />
      <meta property="og:site_name" content={pageTitle} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={siteUrl} />
      <meta name="twitter:title" content={pageTitle} />
      <meta name="twitter:description" content={pageDescription} />
      <meta name="twitter:image" content={defaultImage} />
    </Helmet>
  );
};

export default SiteHead;
